import { AnimatePresence, motion } from 'motion/react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { MEADOW_SCENE } from '@/components/blocks/auth/meadow-scene';
import { XStack, YStack } from '@/components/ui';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { cn } from '@/lib/utils';

import {
  type WebOnboardingChoice,
  useWebOnboardingFlow,
} from './web-onboarding.hooks';

const riseIn = (delay: number) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: 'easeOut' as const, delay },
});

const Bubble = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'w-fit max-w-xs rounded-2xl border border-border-strong px-4 py-2',
      'font-serif text-base text-foreground leading-snug',
      className
    )}
  >
    {children}
  </div>
);

const AssistantRow = ({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) => (
  <motion.div {...riseIn(delay)}>
    <XStack className="gap-3">
      <span className="mt-1.5 shrink-0">
        <AppLogo size={20} />
      </span>
      <Bubble>{children}</Bubble>
    </XStack>
  </motion.div>
);

const UserRow = ({ choice }: { choice: WebOnboardingChoice }) => {
  const IntegrationIcon = choice.integration
    ? getIntegrationIcon(choice.integration.toolkitName)
    : null;
  return (
    <motion.div {...riseIn(0)} className="flex justify-end">
      <Bubble className="flex items-center gap-2 bg-surface-hover">
        {IntegrationIcon && <IntegrationIcon className="size-4 shrink-0" />}
        {choice.label}
      </Bubble>
    </motion.div>
  );
};

const ChoiceChip = ({
  label,
  toolkitName,
  onSelect,
}: {
  label: string;
  toolkitName?: string;
  onSelect: () => void;
}) => {
  const IntegrationIcon = toolkitName ? getIntegrationIcon(toolkitName) : null;

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex items-center gap-2 rounded-2xl border border-border-strong px-4 py-1.5',
        'font-serif text-base text-foreground',
        'transition-colors hover:bg-surface-hover focus-ring'
      )}
      layout
      variants={{
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 },
      }}
      exit={{ opacity: 0, y: -6, transition: { duration: 0.15 } }}
      whileTap={{ scale: 0.98 }}
    >
      {IntegrationIcon && <IntegrationIcon className="size-4 shrink-0" />}
      {label}
    </motion.button>
  );
};

export const WebOnboarding = () => {
  const { phase, addedIntegrations, remainingChoices, selectChoice } =
    useWebOnboardingFlow();
  const hasAddedAny = addedIntegrations.length > 0;
  const isLeaving = phase === 'finishing';

  return (
    <div className="relative h-dvh w-full overflow-hidden bg-[#1B1C15]">
      <img
        src={MEADOW_SCENE.imageUrl}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: MEADOW_SCENE.objectPosition }}
      />
      <div
        className="absolute inset-0"
        style={{ background: MEADOW_SCENE.wash }}
      />

      <div className="relative z-10 flex h-full w-full items-center justify-center p-4 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.99 }}
          animate={
            isLeaving
              ? { opacity: 0, y: -8, scale: 0.99 }
              : { opacity: 1, y: 0, scale: 1 }
          }
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className={cn(
            'w-full max-w-3xl h-[38rem] max-h-[85dvh] overflow-y-auto',
            'rounded-[20px] bg-background',
            'shadow-[0_30px_80px_-24px_rgba(27,28,21,0.6)]'
          )}
        >
          <YStack className="gap-6 p-8 pt-16 md:p-16 md:pt-24">
            <YStack className="gap-4">
              <AssistantRow delay={0.2}>
                Hello, I&rsquo;m littlebird
              </AssistantRow>
              <motion.div {...riseIn(0.7)} className="pl-8">
                <Bubble>
                  I can help organize your life. How can we start?
                </Bubble>
              </motion.div>
            </YStack>

            {addedIntegrations.map((choice) => (
              <YStack key={choice.id} className="gap-4">
                <UserRow choice={choice} />
                <AssistantRow delay={0.35}>
                  It should be ready soon. Anything else you&rsquo;d like to
                  add?
                </AssistantRow>
              </YStack>
            ))}

            {phase === 'error' && (
              <AssistantRow delay={0.1}>
                Hmm, something went wrong finishing up. Give it another try.
              </AssistantRow>
            )}

            <AnimatePresence mode="popLayout">
              {!isLeaving && (
                <motion.div
                  key={`choices-${addedIntegrations.length}`}
                  className="flex max-w-md flex-wrap gap-3 pl-8"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.08,
                        delayChildren: hasAddedAny ? 0.7 : 1.2,
                      },
                    },
                  }}
                >
                  {remainingChoices.map((choice) => (
                    <ChoiceChip
                      key={choice.id}
                      label={
                        choice.id === 'chat' && hasAddedAny
                          ? 'That\u2019s all for now'
                          : choice.label
                      }
                      toolkitName={choice.integration?.toolkitName}
                      onSelect={() => selectChoice(choice)}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </YStack>
        </motion.div>
      </div>
    </div>
  );
};
